/**
 * @documental
 * @export Form
 */
import { ForwardedRef, ReactNode, useEffect } from "react";
import { DefaultValues, FieldErrors, FieldValues, FormProvider, UseFormReturn } from "react-hook-form";

import { AccessibilityProps } from "../../AccessibilityProps";
import { BaseProps } from "../../BaseProps";
import { LoadingGroup } from "../../general/LoadingGroup";
import { EmptyObject } from "../../utilities/EmptyObject";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { noOp } from "../../utilities/noOp";
import { withSubComponents } from "../../utilities/withSubComponents";
import { InputGroup } from "../InputGroup";
import { SubmitGroup } from "../SubmitGroup";
import { FormDrawer } from "./FormDrawer";
import { FormModal } from "./FormModal";
import { useForm } from "./useForm";

import styles from "./styles.module.scss";

export { type FieldValues, type Path, type UseFormProps, type UseFormReturn } from "react-hook-form";

/**
 * The `Form` component properties.
 */
export interface FormProps<TValues extends FieldValues> extends BaseProps, Pick<AccessibilityProps, "aria-label" | "aria-labelledby" | "aria-describedby"> {
  /**
   * The values to initialize the form fields with. If not specified the default values will be initialized
   * with the defaults received from the form's fields.
   */
  readonly defaultValues?: TValues;

  /**
   * Indicates whether descendent inputs should be disabled.
   */
  readonly disabled?: boolean;

  /**
   * Specifies whether descendent inputs should display as read-only.
   */
  readonly readOnly?: boolean;

  /**
   * Provides a reference to the methods returned from the internal useForm call.
   * @deprecated Provide form prop instead.
   */
  readonly methodsRef?: ForwardedRef<UseFormReturn<TValues> | null>;

  /**
   * Return value from useForm.  If not provided, it will be created and accessible via Form context.
   */
  readonly form?: UseFormReturn<TValues>;

  /**
   * The form children, or a template which will be passed the internal form object.
   */
  readonly children?: ReactNode | ((form: UseFormReturn<TValues>) => ReactNode);

  /**
   * The function to call when the form is submitted.
   */
  readonly onSubmit?: (values: TValues) => Promise<void>;

  /**
   * The function to call when a submit error occurs.
   */
  // @ts-ignore
  readonly onInvalid?: (errors: FieldErrors<TValues>) => void;

  /**
   * Specifies whether the inputs should be in loading state.
   */
  readonly loading?: boolean | Promise<unknown>;
}

/**
 * The `Form` component sub components.
 */
export interface FormSubComponents {
  /**
   * Represents a modal version of a Form.
   */
  readonly Modal: typeof FormModal;

  /**
   * Represents a drawer version of a Form.
   */
  readonly Drawer: typeof FormDrawer;
}

/**
 *  A component which groups inputs together, allowing for them to be submitted.
 *
 * `import { Form } from "capitalrx-components-2.0/lib/forms";`
 *
 * ------
 *
 * For your convenience we return the following `React Hook Form` methods when using a template function as a form child.
 *
  ```JS
   clearErrors: (name) => {…}
   control: {
    getFieldState: (name, formState) => {…}
    register: (name, options = {}) => {…}
    unregister: (name, options = {}) => {…}
   }
   formState: {…}
   getFieldState: (name, formState) => {…}
   getValues: (fieldNames) => {…}
   handleSubmit: (onValid, onInvalid) => {…}
   register: (name, options = {}) => {…}
   reset: (formValues, keepStateOptions = {}) => {…}
   resetField: (name, options = {}) => {…}
   setError: (name, error, options) => {…}
   setFocus: (name, options = {}) => {…}
   setValue: (name, value, options = {}) => {…}
   trigger: async (name, options = {}) => {…}
   unregister: (name, options = {}) => {…}
   watch: (name, defaultValue) => {…}
  ```
 * To learn more about the above methods please visit <a href="https://react-hook-form.com">React Hook Form</a>.
 *
 * ##### To get the above methods, do one of the following
 *
   ```js

    import { useRef } from "react";
    ...
    ...

     const YourForm = () => {
       const formRef = useRef(null);

       <Form methodsRef={formRef}...>
         ...
       </Form>
     }
  ```

  or

  ```js

    import { useRef } from "react";
    ...
    ...

     const YourForm = () => {
       <Form ...>
         {(form) => {

           // methods accessible on `form`.

           return (
             ...
           );
         }}
       </Form>
     }
  ```
 */
export const Form = withSubComponents(
  {
    Modal: FormModal,
    Drawer: FormDrawer,
  } as FormSubComponents,
  <TValues extends FieldValues = FieldValues>(props: FormProps<TValues>) => {
    const {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      disabled,
      readOnly,
      children,
      defaultValues,
      form,
      methodsRef,
      onSubmit,
      onInvalid,
      loading = false,
    } = props;

    if (form && methodsRef) {
      throw new Error("Do not use both form and methodsRef; methodsRef is deprecated, provide form only");
    }

    if (form && defaultValues) {
      throw new Error("Do not use both form and defaultValues; provide defaultValues to useForm instead");
    }

    const defaultForm = useForm<TValues>({
      defaultValues: (defaultValues || EmptyObject) as DefaultValues<TValues>,
    });

    const formMethods = form || defaultForm;

    const { handleSubmit, formState } = formMethods;
    const { isDirty, isValid, isSubmitting } = formState;

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.Form,
        "form",
        {
          invalid: !isValid,
          dirty: isDirty,
          submitting: isSubmitting,
          loading: loading,
        },
      ],
      testIdPrefix: "form",
    });

    useEffect(() => {
      if (methodsRef) {
        if (typeof methodsRef === "function") methodsRef(formMethods);
        else methodsRef.current = formMethods;
      }

      return () => {
        if (methodsRef) {
          if (typeof methodsRef === "function") methodsRef(null);
          else methodsRef.current = null;
        }
      };
    }, [methodsRef, formMethods]);

    return (
      <LoadingGroup loading={loading}>
        <FormProvider {...formMethods}>
          <SubmitGroup submitting={isSubmitting}>
            <form
              {...baseHtmlProps}
              onSubmit={handleSubmit(onSubmit ? onSubmit : noOp, onInvalid ? onInvalid : noOp)}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
            >
              <InputGroup disabled={disabled} readOnly={readOnly}>
                {typeof children === "function" ? children(formMethods) : children}
              </InputGroup>
            </form>
          </SubmitGroup>
        </FormProvider>
      </LoadingGroup>
    );
  },
);
